<template>
  <NForm requireMarkPlacement="right" :show-feedback="false" size="medium">
    <NGrid :cols="2" :x-gap="16" :y-gap="16">
      <NFormItemGi v-if="!hideStatus" :label="labels.is_draft__status">
        <CInputPlaceholder
          v-if="showKeepAsIsPlaceholder && !isEditingFieldOrNonEmpty('is_draft__status')"
          @click="enableEditing('is_draft__status', FieldTypes.SELECT)"
        />
        <CSelect
          v-else
          bordered
          :clearable="showKeepAsIsPlaceholder"
          data-field="is_draft__status"
          :filterable="showKeepAsIsPlaceholder"
          :on-clear="
            async () => {
              await nextTick()
              handleBlur('is_draft__status')
            }
          "
          :on-update:value="onChangeStatus"
          :options="statusOptions"
          :value="statusValueAsText"
          @blur="handleBlur('is_draft__status')"
        />
      </NFormItemGi>
    </NGrid>
  </NForm>
</template>

<script setup lang="ts">
import { NForm, NFormItemGi, NGrid } from 'naive-ui'
import { computed, ref, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'

import CInputPlaceholder from '@lib/components/CInputPlaceholder/CInputPlaceholder.vue'
import CSelect from '@lib/components/CSelect/CSelect.vue'

import { MetadataTemplateFieldTypeEnum as FieldTypes } from '@openapi'

type StatusOption = 'draft' | 'ready'

export interface Form {
  is_draft?: boolean
  [key: string]: any
}

const props = withDefaults(
  defineProps<{
    hideStatus?: boolean
    modelValue: Form
    showKeepAsIsPlaceholder?: boolean
  }>(),
  {
    hideStatus: false,
  },
)

const emit = defineEmits<{
  'update:modelValue': [form: Form]
}>()

const { t } = useI18n()

const editingField = ref<string | null>(null)
const labels: Record<string, string> = {
  is_draft__status: t('canvasUI.components.CAttributesForm.labels.status'),
}
const statusOptions = [
  { value: 'draft', label: t('canvasUI.components.CAttributesForm.options.status.draft') },
  { value: 'ready', label: t('canvasUI.components.CAttributesForm.options.status.ready') },
]

const form = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})

const statusValueAsText = computed({
  get: () => {
    if (form.value.is_draft === undefined) return null
    return form.value.is_draft ? 'draft' : 'ready'
  },
  set: (value: StatusOption | null) => {
    if (value === null) {
      form.value.is_draft = undefined
    } else {
      form.value.is_draft = value === 'draft'
    }
  },
})

function enableEditing(fieldName: string, fieldType: FieldTypes) {
  editingField.value = fieldName

  nextTick(() => {
    const input = document.querySelector(`[data-field="${fieldName}"]`) as HTMLElement
    if (!input) return

    switch (fieldType) {
      case FieldTypes.SELECT:
      case FieldTypes.MULTI_SELECT: {
        const selectionInput = input.querySelector('.n-base-selection-input') as HTMLElement
        selectionInput?.click()
        break
      }
      default:
        console.warn(`Unhandled field type: ${fieldType}`)
    }
  })
}

async function handleBlur(fieldName: string) {
  await nextTick()

  if (fieldName === 'is_draft__status' && form.value.is_draft === undefined) {
    editingField.value = null
    return
  }

  if (!form.value[fieldName]) {
    editingField.value = null
    form.value[fieldName] = undefined
  }
}

function isEditingFieldOrNonEmpty(fieldName: string) {
  if (fieldName === 'is_draft__status') {
    return form.value.is_draft !== undefined || editingField.value === fieldName
  }
  return editingField.value === fieldName || !!form.value[fieldName]
}

function onChangeStatus(value: StatusOption | null) {
  emit('update:modelValue', {
    ...form.value,
    is_draft: value === null ? undefined : value === 'draft',
  })
}
</script>
